/**
 * The developer worlds (ADR-080): worlds played only on a developer's machine, each through a map server of its
 * own on its own development gateway port, and never published. The game page finds a developer world's gateway
 * by the world's id, the map server keeps no character positions in one, and every publish path refuses one.
 */

/**
 * What the development tools know of one developer world: the port its development gateway listens on.
 */
export interface DeveloperWorld {
  readonly port: number;
}

/**
 * Every developer world by id: `sandbox`, the developer map, on port 7001 beside Aethervale's 7000, and `stress`, the
 * generated density stress world (P2.0-T4, ADR-080's amendment of 2026-10-03), on port 7002.
 */
export const DEVELOPER_WORLDS: Readonly<Record<string, DeveloperWorld>> = Object.freeze({ sandbox: Object.freeze({ port: 7001 }), stress: Object.freeze({ port: 7002 }) });

/**
 * The developer world a world id names.
 *
 * @param worldId The world id.
 * @returns The developer world, or null when the id names a world that is published and played on live; a name
 *   an object inherits, such as `constructor`, names none.
 */
export function developerWorld(worldId: string): DeveloperWorld | null {
  return Object.hasOwn(DEVELOPER_WORLDS, worldId) ? (DEVELOPER_WORLDS[worldId] as DeveloperWorld) : null;
}

/**
 * The one sentence a publish, a ship, a rollback or the publish command line refuses a developer world with,
 * checked before anything is uploaded or re-pointed (ADR-080 decision 3).
 *
 * @param worldId The world asked to be published.
 * @returns The sentence, or null when the world may be published.
 */
export function developerWorldRefusal(worldId: string): string | null {
  const world = developerWorld(worldId);
  return world === null ? null : `${worldId} is a developer world (ADR-080): it is played locally through its own map server on port ${world.port} and is never published, so nothing was uploaded or re-pointed`;
}

/**
 * The publish guard: throws before a publish path uploads or re-points anything for a developer world.
 *
 * @param worldId The world asked to be published.
 * @throws Error with `developerWorldRefusal`'s sentence when the world is a developer world.
 */
export function requirePublishable(worldId: string): void {
  const refusal = developerWorldRefusal(worldId);
  if (refusal !== null) {
    throw new Error(refusal);
  }
}
